import React from 'react';
import Checkbox from '@mui/material/Checkbox';
import { BulkHitVisibilityOperation } from '../../../BiteDB/Tables/bulkHitSelection';
import {
  EntityHitSortKey,
  SortDirection,
} from '../../../BiteDB/Tables/entityHitList';

interface IProps {
  isProduct?: boolean;
  showMatchScore?: boolean;
  showBulkSelection?: boolean;
  isPageSelected?: boolean;
  isPageIndeterminate?: boolean;
  onTogglePageSelection?: () => void;
  bulkOperation?: BulkHitVisibilityOperation;
  sortKey?: EntityHitSortKey;
  sortDirection?: SortDirection;
  onSort?: (key: EntityHitSortKey) => void;
  layout?: 'default' | 'entityHits';
}

const SortIcon = ({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDirection;
}) => {
  if (!active) {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
      >
        <path
          d='M8 4V12M8 4L4 8M8 4L12 8'
          stroke='var(--card-text)'
          strokeOpacity='0.5'
          strokeWidth='1.33'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    );
  }
  if (direction === 'asc') {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
      >
        <path
          d='M8 12.6666V3.33331M8 3.33331L3.33333 7.99998M8 3.33331L12.6667 7.99998'
          stroke='var(--card-text)'
          strokeWidth='1.33'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    );
  }
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
    >
      <path
        d='M8 3.33331V12.6666M8 12.6666L3.33333 7.99998M8 12.6666L12.6667 7.99998'
        stroke='var(--card-text)'
        strokeWidth='1.33'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const Level3Headers: React.FC<IProps> = ({
  isProduct = false,
  showMatchScore = false,
  showBulkSelection = false,
  isPageSelected = false,
  isPageIndeterminate = false,
  onTogglePageSelection,
  bulkOperation = 'hide',
  sortKey,
  sortDirection = 'desc',
  onSort,
  layout = 'default',
}) => {
  const renderHead = (label: string, key?: EntityHitSortKey) => {
    if (!onSort || !key) {
      return <div className='results-table-frame-tab-body-col-cell1-head'>{label}</div>;
    }
    return (
      <div
        className='results-table-frame-tab-body-col-cell1-head'
        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        onClick={() => onSort(key)}
      >
        {label}{' '}
        <SortIcon active={sortKey === key} direction={sortDirection} />
      </div>
    );
  };

  return (
    <>
      <div className='results-table-frame-tab-body-col'>
        {showBulkSelection && (
          <div className='results-table-frame-tab-body-col-empty bulk-hit-select-cell'>
            <Checkbox
              checked={isPageSelected}
              indeterminate={isPageIndeterminate}
              onChange={onTogglePageSelection}
              size='small'
              inputProps={{
                'aria-label': `Select all hits to ${bulkOperation}`,
              }}
              sx={{ color: 'var(--text-secondary)', padding: 0 }}
            />
          </div>
        )}
        <div className='results-table-frame-tab-body-col-cell1'>
          {renderHead(
            !isProduct ? (layout === 'entityHits' ? 'Entity Name' : 'Entity') : 'Product Name',
            'name',
          )}
        </div>
        {isProduct && (
          <div className='results-table-frame-tab-body-col-cell1'>
            <div className='results-table-frame-tab-body-col-cell1-head'>
              HS Code
            </div>
          </div>
        )}
        {!isProduct && (
          <div className='results-table-frame-tab-body-col-cell1'>
            {renderHead('Entity Country', 'entityCountry')}
          </div>
        )}
        {layout === 'entityHits' && !isProduct && (
          <div className='results-table-frame-tab-body-col-cell1'>
            {renderHead('Lens', 'lens')}
          </div>
        )}
        {layout === 'entityHits' && !isProduct && (
          <div className='results-table-frame-tab-body-col-cell1'>
            {renderHead('List Name', 'list')}
          </div>
        )}
        <div className='results-table-frame-tab-body-col-cell1'>
          {renderHead('Source Country', 'sourceCountry')}
        </div>
        {layout !== 'entityHits' && (
          <div className='results-table-frame-tab-body-col-cell1'>
            {renderHead('Agency', 'agency')}
          </div>
        )}
        {layout !== 'entityHits' && (
          <div className='results-table-frame-tab-body-col-cell1'>
            {renderHead('List', 'list')}
          </div>
        )}
        {showMatchScore && !isProduct && (
          <div className='results-table-frame-tab-body-col-cell1'>
            {renderHead('Match Score', 'matchScore')}
          </div>
        )}
        <div className='results-table-frame-tab-body-col-cell1'>
          {renderHead(
            layout === 'entityHits' ? 'Date Updated' : 'Date Added',
            'dateAdded',
          )}
        </div>
        <div className='view-history-head-frame-cell-emptys'>
          <div className='view-history-head-frame-cell-cont'>
            <div className='view-history-head-frame-cell-cont-text'>
              Remove Hit
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Level3Headers;
